import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { AnalyticsEvent } from '~/modules/services/analyticsService';
import { SearchTotals } from '~/modules/stores/searchStore';
import {
    PLATFORM_FILTER_ENGAGEMENT_ELEMENT_TYPE,
    SEARCH_TYPE_SWITCH_ENGAGEMENT_ELEMENT_TYPE,
} from './filterEngagementConstants';
import { GA4FilterClickEvent, GA4FilterEngagementEvent, GTMBaseEvent, GtmEventType } from './gtmEventType';

export type FilterClickAnalyticsEvent = {
    checked: boolean;
    searchContext: {
        sectionLabel: string;
        keyword: string;
        url: string;
        optionLabel: string;
        searchResultsCountPerType: SearchTotals;
        bucket: string;
    };
} & AnalyticsEvent;

const ENGAGEMENT_ELEMENT_TYPES = [PLATFORM_FILTER_ENGAGEMENT_ELEMENT_TYPE, SEARCH_TYPE_SWITCH_ENGAGEMENT_ELEMENT_TYPE];

const isEngagement = (elementType: string | undefined): boolean =>
    !!elementType && ENGAGEMENT_ELEMENT_TYPES.includes(elementType);

export class GtmFilterClickType extends GtmEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            (((event.elementType === 'filter' || event.elementType === 'chip') && !event.sortLabel) ||
                isEngagement(event.elementType))
        );
    }

    getBody(event: FilterClickAnalyticsEvent): GA4FilterClickEvent | GA4FilterEngagementEvent {
        if (isEngagement(event.elementType)) {
            return this.engagementBody(event as unknown as GTMBaseEvent);
        }

        const { checked, searchContext } = event;
        const { sectionLabel, keyword, optionLabel, searchResultsCountPerType, bucket } = searchContext;
        const filterAction = checked ? 'add' : 'remove';

        return {
            event: 'custEv_contentClick',
            click_text: optionLabel,
            element_type: 'filter',
            filter_label: sectionLabel,
            filter_action: filterAction,
            search_term: keyword,
            search_type: 'site',
            search_result_count:
                (searchResultsCountPerType?.apps || 0) +
                (searchResultsCountPerType?.consultants || 0) +
                (searchResultsCountPerType?.agents || 0) +
                (searchResultsCountPerType?.externalAgents || 0),
            search_category: bucket || 'all',
            app_results: searchResultsCountPerType?.apps,
            consultant_results: searchResultsCountPerType?.consultants,
            agent_results: searchResultsCountPerType?.agents,
        };
    }

    private engagementBody(event: GTMBaseEvent): GA4FilterEngagementEvent {
        const totals = (event.totals as Partial<SearchTotals>) || {};
        const apps = totals.apps || 0;
        const consultants = totals.consultants || 0;
        const agents = totals.agents || 0;
        const slack = totals.slack || 0;
        const externalAgents = totals.externalAgents || 0;
        const label = (event.filterLabel as string) || '';
        const isPlatform = event.elementType === PLATFORM_FILTER_ENGAGEMENT_ELEMENT_TYPE;
        const searchResultCount = apps + consultants + agents + slack + externalAgents;

        // Union of the existing custEv_contentClick this replaces (element_type
        // and click_text kept as-is; platformFilter also keeps content_category)
        // plus the new AI fields. Both search_result_count (existing) and
        // search_results_count (new spec) are emitted, each the total across all
        // platforms (apps + consultants + agents + slack + External Agents).
        return {
            event: 'custEv_contentClick',
            element_type: isPlatform ? 'platformFilter' : 'filter',
            click_text: (event.clickText as string) || label,
            ...(isPlatform ? { content_category: (event.contentCategory as string) || '' } : {}),
            filter_label: label,
            filter_action: 'add',
            search_term: (event.keyword as string) || '',
            search_type: 'site',
            search_category: 'all',
            search_result_count: searchResultCount,
            search_results_count: searchResultCount,
            app_results: apps,
            consultant_results: consultants,
            agent_results: agents,
            // Salesforce platform = Apps, matching platformFilter.ts. Agents are a
            // `tech=force/copilot` subset of apps, so they are not added again here.
            salesforce_results: apps,
            slack_results: slack,
            ai_summary_results: (event.aiSummaryResults as number) || 0,
        };
    }
}
